import React from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { useTranslation } from 'react-i18next';

const widths = ['72%', '54%', '86%', '40%', '64%', '48%'];

/**
 * Grey placeholder rows, the same height as the real rows, shown while a
 * list loads for the first time (keep already-loaded rows on screen while
 * refreshing instead). Reads "Loading..." to screen readers.
 *
 *   <SkeletonRows rows={6} columns={4} rowHeight={52} avatar />
 */
export default function SkeletonRows({ rows = 6, columns = 3, rowHeight = 52, avatar = false, sx }) {
  const { t } = useTranslation();
  return (
    <Box role="status" aria-label={t('loading', { defaultValue: 'Loading…' })} sx={{ width: '100%', ...sx }}>
      {Array.from({ length: rows }).map((_, r) => (
        <Box
          key={`sk-${r}`}
          aria-hidden="true"
          sx={{ display: 'flex', alignItems: 'center', gap: 2, minHeight: rowHeight, px: 2, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          {avatar ? <Skeleton variant="circular" width={36} height={36} sx={{ flex: 'none' }} /> : null}
          {Array.from({ length: columns }).map((__, col) => (
            <Box key={`sk-${r}-${col}`} sx={{ flex: col === 0 ? 2 : 1, minWidth: 0 }}>
              <Skeleton variant="rounded" height={12} width={widths[(r + col) % widths.length]} sx={{ borderRadius: '6px' }} />
              {col === 0 ? <Skeleton variant="rounded" height={10} width="40%" sx={{ mt: 1, borderRadius: '6px' }} /> : null}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

/**
 * The same placeholders inside an MUI <TableBody>, so the real header stays
 * visible while the body loads.
 *
 *   <TableBody>{loading ? <TableSkeletonRows rows={6} columns={7} /> : rows}</TableBody>
 */
export function TableSkeletonRows({ rows = 6, columns = 4, rowHeight = 52 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <TableRow key={`tsk-${r}`} aria-hidden="true" sx={{ height: rowHeight }}>
          {Array.from({ length: columns }).map((__, col) => (
            <TableCell key={`tsk-${r}-${col}`}>
              <Skeleton variant="rounded" height={12} width={widths[(r + col) % widths.length]} sx={{ borderRadius: '6px' }} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}
